import React, { useState } from 'react';
import { Activity, CheckCircle, Heart, Info, AlertTriangle, Send, ChevronRight, ChevronLeft } from 'lucide-react';

const CI = { brombeer: '#85366C', orchidee: '#DC91D0', blue: '#2DBAE8', green: '#8CC53F', red: '#ED2979' };

const APPOINTMENT_DATA = {
  patientName: 'Max Mustermann',
  dob: '1945-05-12', 
  date: '2026-05-12',
  time: '10:30',
  type: 'video' 
};

const CHALLENGE_TAGS = {
  kinder: ['Entwicklungsverzögerung', 'Schulbegleitung / Kita fehlt', 'Hilfsmittel nötig', 'Eltern überlastet', 'Behörden / Kasse'],
  erwachsene: ['Beruf & Pflege vereinbaren', 'Psychische Belastung', 'Wohnraumanpassung', 'Finanzielle Sorgen', 'Schmerzen'],
  senioren: ['Sturzgefahr', 'Kognition / Demenz', 'Inkontinenz', 'Nächtliche Unruhe', 'Schlucken / Ernährung', 'Hilfe bei Körperpflege']
};

const CARE_SITUATION_TAGS = [
  'Angehörige stark gefordert', 'Aktuell sicher aufgestellt', 'Ambulanter Pflegedienst',
  'Alltagsbegleitung', 'Verhinderungspflege', 'Nachbarschaftshilfe', 'Tagespflege'
];

export default function VorbereitungsPortal() {
  const [step, setStep] = useState(1);

  const getAgeCategory = (dob) => {
    const age = new Date().getFullYear() - new Date(dob).getFullYear();
    if (age < 18) return 'kinder';
    if (age < 65) return 'erwachsene';
    return 'senioren';
  };

  const ageCategory = getAgeCategory(APPOINTMENT_DATA.dob);
  const currentChallenges = CHALLENGE_TAGS[ageCategory];

  const [selectedChallenges, setSelectedChallenges] = useState([]);
  const [challengeNotes, setChallengeNotes] = useState('');
  
  const [selectedCare, setSelectedCare] = useState([]);
  const [careNotes, setCareNotes] = useState('');
  
  const [notes, setNotes] = useState('');

  const toggleChallenge = (tag) => {
    setSelectedChallenges(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  const toggleCare = (tag) => {
    setSelectedCare(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  const nextStep = () => setStep(prev => prev + 1);
  const prevStep = () => setStep(prev => prev - 1);
  const handleSubmit = (e) => { e.preventDefault(); setStep(4); };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center py-10 px-4 font-sans">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 flex flex-col min-h-[550px] max-h-[85vh]">
        
        <div className="p-6 text-white text-center relative shrink-0 transition-colors duration-500" style={{ backgroundColor: CI.brombeer }}>
          <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3 backdrop-blur-sm">
            <Activity className="w-6 h-6 text-white" />
          </div>
          <h2 className="text-xl font-bold">Vorbereitung auf unseren Termin</h2>
          <p className="text-sm opacity-90 mt-1">Helfen Sie uns, Sie bestmöglich zu beraten.</p>
        </div>

        {step < 4 && (
          <div className="flex h-2 bg-gray-100 shrink-0">
            <div className="transition-all duration-500 h-full" style={{ width: `${(step / 3) * 100}%`, backgroundColor: CI.blue }}></div>
          </div>
        )}

        <div className="p-6 md:p-8 flex-1 flex flex-col overflow-hidden relative">
          
          {step === 1 && (
            <div className="animate-fade-in flex-1 flex flex-col overflow-y-auto pr-2">
              <div className="p-4 border border-blue-100 bg-blue-50/50 rounded-xl mb-5">
                <div className="flex gap-3 items-start">
                  <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                  <div className="text-sm text-gray-700 leading-relaxed">
                    <strong>Guten Tag!</strong><br />
                    Bald findet unser Beratungstermin für <strong>{APPOINTMENT_DATA.patientName}</strong> statt. 
                    Bitte beantworten Sie uns kurz 3 kleine Fragen zur Vorbereitung. 
                    <span className="text-gray-500 mt-1 block">Das Ausfüllen ist komplett <strong>freiwillig</strong>.</span>
                  </div>
                </div>
              </div>

              <h3 className="text-lg font-bold text-gray-800 mb-1">1. Besondere Herausforderungen?</h3>
              <p className="text-sm text-gray-500 mb-4">Was beschäftigt Sie im Alltag am meisten? (Mehrfachauswahl)</p>
              
              <div className="flex flex-wrap gap-2">
                {currentChallenges.map((tag) => {
                  const isSelected = selectedChallenges.includes(tag);
                  return (
                    <button
                      key={tag} type="button" onClick={() => toggleChallenge(tag)}
                      className={`px-3 py-2 rounded-lg text-sm font-bold border transition-all text-left shadow-sm ${isSelected ? 'bg-blue-600 text-white border-blue-600 scale-105 shadow-md' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-400 hover:bg-blue-50'}`}
                    >
                      {isSelected && <CheckCircle className="w-4 h-4 inline-block mr-1 -mt-0.5" />} {tag}
                    </button>
                  );
                })}
              </div>

              <div className="mt-5">
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Weitere Anmerkungen (Optional)</label>
                <textarea 
                  value={challengeNotes} onChange={(e) => setChallengeNotes(e.target.value)}
                  placeholder="Gibt es noch etwas Spezielles zu Ihren Herausforderungen?"
                  className="w-full h-20 p-3 border border-gray-300 rounded-xl text-sm text-gray-800 focus:outline-none focus:ring-2 focus:border-transparent resize-y"
                  style={{ '--tw-ring-color': CI.blue }}
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="animate-fade-in flex-1 flex flex-col overflow-y-auto pr-2">
              <h3 className="text-lg font-bold text-gray-800 mb-1">2. Aktuelle Pflegesituation?</h3>
              <p className="text-sm text-gray-500 mb-4">Welches Netzwerk unterstützt Sie bereits? (Mehrfachauswahl)</p>
              
              <div className="flex flex-wrap gap-2">
                {CARE_SITUATION_TAGS.map((tag) => {
                  const isSelected = selectedCare.includes(tag);
                  return (
                    <button
                      key={tag} type="button" onClick={() => toggleCare(tag)}
                      className={`px-3 py-2 rounded-lg text-sm font-bold border transition-all text-left shadow-sm ${isSelected ? 'bg-green-600 text-white border-green-600 scale-105 shadow-md' : 'bg-white text-gray-600 border-gray-200 hover:border-green-500 hover:bg-green-50'}`}
                    >
                      {isSelected && <CheckCircle className="w-4 h-4 inline-block mr-1 -mt-0.5" />} {tag}
                    </button>
                  );
                })}
              </div>

              <div className="mt-5">
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Weitere Anmerkungen (Optional)</label>
                <textarea 
                  value={careNotes} onChange={(e) => setCareNotes(e.target.value)}
                  placeholder="Wer unterstützt noch? Gibt es Ausfälle bei Pflegediensten?"
                  className="w-full h-20 p-3 border border-gray-300 rounded-xl text-sm text-gray-800 focus:outline-none focus:ring-2 focus:border-transparent resize-y"
                  style={{ '--tw-ring-color': CI.green }}
                />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="animate-fade-in flex-1 flex flex-col overflow-y-auto pr-2">
              <h3 className="text-lg font-bold text-gray-800 mb-2">3. Was brennt Ihnen noch unter den Nägeln?</h3>
              <p className="text-sm text-gray-500 mb-5">Gibt es konkrete Fragen oder Sorgen, die wir im Termin unbedingt besprechen sollten?</p>
              
              <textarea 
                value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="Ihre persönlichen Notizen..."
                className="w-full h-32 p-4 border border-gray-300 rounded-xl text-sm text-gray-800 focus:outline-none focus:ring-2 focus:border-transparent resize-y mb-6"
                style={{ '--tw-ring-color': CI.brombeer }}
              />

              <div className="flex gap-3 items-start p-4 bg-gray-50 rounded-xl border border-gray-200 mt-auto">
                <AlertTriangle className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" />
                <p className="text-xs text-gray-500 leading-relaxed">
                  <strong>Datenschutzhinweis:</strong> Ihre Gesundheitsdaten werden nach höchsten DSGVO-Standards verschlüsselt übertragen. Sie sind ausschließlich für Ihren Pflegeberater einsehbar.
                </p>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="animate-fade-in flex-1 flex flex-col items-center justify-center text-center p-2">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner"><Heart className="w-10 h-10 text-green-600" /></div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Vielen Dank!</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Ihre Angaben wurden sicher verschlüsselt an Ihren Pflegeberater übermittelt. 
                Wir haben diese Notizen in Ihrer Akte hinterlegt und können uns nun optimal auf unser Gespräch am <strong>{new Date(APPOINTMENT_DATA.date).toLocaleDateString('de-DE')}</strong> vorbereiten.
              </p>
              <p className="text-sm font-bold text-gray-500">Sie können dieses Fenster nun einfach schließen.</p>
            </div>
          )}

          {step < 4 && (
            <div className="mt-8 pt-4 border-t border-gray-100 flex justify-between gap-4 shrink-0 bg-white">
              {step > 1 ? (
                <button onClick={prevStep} className="px-5 py-3 rounded-xl font-bold text-gray-600 hover:bg-gray-100 flex items-center gap-1 transition-colors border border-gray-200"><ChevronLeft className="w-5 h-5" /> Zurück</button>
              ) : <div></div>}
              
              {step < 3 ? (
                <button onClick={nextStep} className="px-8 py-3 rounded-xl font-bold text-white flex items-center gap-1 shadow-md transition-all hover:-translate-y-0.5" style={{ backgroundColor: CI.blue }}>Weiter <ChevronRight className="w-5 h-5" /></button>
              ) : (
                <button onClick={handleSubmit} className="px-6 py-3 rounded-xl font-bold text-white flex items-center gap-2 shadow-lg transition-all hover:-translate-y-0.5 hover:shadow-xl" style={{ backgroundColor: CI.brombeer }}><Send className="w-4 h-4" /> Angaben übermitteln</button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
